import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubWorkspaceBody, HubWorkspaceHeader, HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

type Tier1ScriptRow = { issueOptionId: number; label: string; prompts: string[] };

const longTextClassName =
  "min-h-40 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100";

function useTier1Scripts() {
  return useQuery({
    queryKey: ["quality", "tier-1-scripts"],
    queryFn: () => apiFetch<{ rows: Tier1ScriptRow[] }>("/api/quality/tier-1-scripts"),
  });
}

export function QualityTier1ScriptsPage() {
  const canEdit = useHubModuleAdmin("quality");
  const navigate = useNavigate();
  const scripts = useTier1Scripts();
  const rows = (scripts.data?.rows ?? []).filter((row) => row.prompts.length > 0);
  const columns = useMemo<HubDataTableColumn<Tier1ScriptRow>[]>(
    () => [
      { id: "label", header: "Tier 1 issue", sortValue: (row) => row.label, cell: (row) => row.label },
      {
        id: "prompts",
        header: "Prompts",
        align: "right",
        sortValue: (row) => row.prompts.length,
        cell: (row) => <span className="tabular-nums">{row.prompts.length}</span>,
      },
      {
        id: "edit",
        header: "",
        sortValue: () => "",
        cell: (row) => (
          <Button type="button" size="sm" variant="secondary" onClick={() => navigate(`/quality/settings/tier-1-scripts/${row.issueOptionId}`)}>
            Edit
          </Button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel="Tier 1 scripts"
          hoverHint="One prompt per line. A script is used when an NCR has that tier 1 issue."
        >
          Tier 1 scripts
        </PageTitleWithHelp>
        {canEdit ? (
          <Button type="button" onClick={() => navigate("/quality/settings/tier-1-scripts/new")}>
            Add new tier 1 script
          </Button>
        ) : null}
      </HubWorkspaceHeader>
      <HubWorkspaceBody className="overflow-hidden">
        <HubDataTable
          className="min-h-0 w-full flex-1 max-h-none lg:max-h-none"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.issueOptionId}
          loading={scripts.isLoading}
          error={scripts.error instanceof Error ? scripts.error.message : null}
          empty="No tier 1 scripts."
          defaultSortKey="label"
          defaultSortDir="asc"
        />
      </HubWorkspaceBody>
    </HubWorkspacePage>
  );
}

export function QualityTier1ScriptEditPage() {
  const { issueOptionId } = useParams();
  const isNew = issueOptionId == null;
  const canEdit = useHubModuleAdmin("quality");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const scripts = useTier1Scripts();
  const rows = scripts.data?.rows ?? [];
  const existing = rows.find((row) => String(row.issueOptionId) === issueOptionId);
  const available = rows.filter((row) => row.prompts.length === 0);
  const [pickedId, setPickedId] = useState("");
  const [search, setSearch] = useState("");
  const [prompts, setPrompts] = useState<string | null>(null);
  const promptsValue = prompts ?? existing?.prompts.join("\n") ?? "";
  const targetId = isNew ? Number(pickedId) : Number(issueOptionId);

  const save = useMutation({
    mutationFn: async () => {
      if (!Number.isInteger(targetId) || targetId < 1) throw new ApiError(400, "Choose a tier 1 issue.");
      const lines = promptsValue.split("\n").map((line) => line.trim()).filter((line) => line.length > 0);
      await apiFetch(`/api/quality/tier-1-scripts/${targetId}`, {
        method: "PUT",
        body: { prompts: lines, active: true },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quality", "tier-1-scripts"] });
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
      navigate("/quality/settings/tier-1-scripts");
    },
    onError: (error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Could not save the tier 1 script." });
    },
  });

  return (
    <div className="space-y-4">
      <PageTitleWithHelp
        breadcrumbLabel={isNew ? "New tier 1 script" : (existing?.label ?? "Tier 1 script")}
        hoverHint="One prompt per line. A script is used when an NCR has that tier 1 issue."
      >
        {isNew ? "New tier 1 script" : "Edit tier 1 script"}
      </PageTitleWithHelp>
      {!isNew && scripts.isSuccess && existing == null ? <p className="text-sm text-slate-600">This tier 1 script was not found.</p> : null}
      {isNew ? (
        <div className="max-w-xl">
          <SearchableSelect
            value={pickedId}
            options={available.map((row) => ({ value: String(row.issueOptionId), label: row.label }))}
            searchValue={search}
            onSearchChange={setSearch}
            disabled={!canEdit}
            placeholder="Tier 1 issue"
            searchPlaceholder="Search tier 1 issues"
            emptyMessage="Every tier 1 issue already has a script"
            onValueChange={(option) => setPickedId(option?.value ?? "")}
          />
        </div>
      ) : (
        <p className="text-sm text-slate-800">{existing?.label}</p>
      )}
      <label className="block text-sm text-slate-800">
        Prompts
        <textarea
          aria-label="Tier 1 script prompts"
          className={`${longTextClassName} mt-1`}
          placeholder="One prompt per line"
          value={promptsValue}
          disabled={!canEdit}
          onChange={(event) => setPrompts(event.target.value)}
        />
      </label>
      {canEdit ? (
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </Button>
      ) : null}
    </div>
  );
}
